import { useEffect, useRef } from "react";
import type * as Monaco from "monaco-editor";
import { useThemeSwitch, useMonacoThemeVariant } from "sj-ui-toolkit/src/theme";

/** The `monaco` namespace object handed to the editor `onMount` callback. */
type MonacoNamespace = typeof import("monaco-editor");
type CodeEditor = Monaco.editor.IStandaloneCodeEditor;
type ThemeData = Monaco.editor.IStandaloneThemeData;

type ThemeMode = "dark" | "light";

// Theme file names must match files in public/monaco-themes/
const THEMES_BY_VARIANT: Record<string, Record<ThemeMode, string>> = {
  default: { dark: "brilliance-dull", light: "chrome-devtools" },
  github: { dark: "github-dark", light: "github-light" },
};

const ALL_THEME_NAMES = Object.values(THEMES_BY_VARIANT).flatMap(pair => [pair.dark, pair.light]);

const resolveThemeName = (variant: string, appliedTheme: string | null): string => {
  const mode: ThemeMode = appliedTheme === "atlas-dark" ? "dark" : "light";
  const pair = THEMES_BY_VARIANT[variant] ?? THEMES_BY_VARIANT.default;
  return pair[mode];
};

const applyEditorBackgroundVar = (themeJson: ThemeData | undefined) => {
  const bg = themeJson?.colors?.["editor.background"];
  if (bg) {
    document.documentElement.style.setProperty("--monaco-editor-background", bg);
  }
};

export const useMonacoThemes = () => {
  const editorRef = useRef<CodeEditor | null>(null);
  const monacoRef = useRef<MonacoNamespace | null>(null);
  const themesRef = useRef<Record<string, ThemeData>>({});

  const { appliedTheme } = useThemeSwitch();
  const { variant } = useMonacoThemeVariant();
  const monacoTheme = resolveThemeName(variant, appliedTheme);

  const handleEditorMount = async (editor: CodeEditor, monaco: MonacoNamespace) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Set aria-label for the editor's (IME) textarea to improve accessibility
    const textarea = editor.getDomNode()?.querySelector("textarea");
    if (textarea) {
      textarea.setAttribute("aria-label", "Code editor");
    }

    try {
      const responses = await Promise.all(
        ALL_THEME_NAMES.map(name => fetch(`/monaco-themes/${name}.json`))
      );
      const themes: ThemeData[] = await Promise.all(responses.map(r => r.json()));

      ALL_THEME_NAMES.forEach((name, i) => {
        monaco.editor.defineTheme(name, themes[i]);
        themesRef.current[name] = themes[i];
      });
      monaco.editor.setTheme(monacoTheme);
      applyEditorBackgroundVar(themesRef.current[monacoTheme]);
    } catch (error) {
      console.error("Failed to load Monaco themes:", error);
    }
  };

  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(monacoTheme);
      applyEditorBackgroundVar(themesRef.current[monacoTheme]);
    }
  }, [monacoTheme]);

  return {
    editorRef,
    monacoTheme,
    handleEditorMount,
  };
};
